import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Inventory2,
  Email,
  HourglassEmpty,
  Search as SearchIcon,
  ContentCopy,
  SupportAgent
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../../common/SEO';
import { apiList, invokeApi, invokeGetApi } from '../../../services/apiServices';

const statusConfig = {
  delivered: { label: 'Delivered', color: 'success', icon: <CheckCircle />, activeStep: 4 },
  completed: { label: 'Delivered', color: 'success', icon: <CheckCircle />, activeStep: 4 },
  processing: { label: 'Processing', color: 'warning', icon: <HourglassEmpty />, activeStep: 2 },
  shipped: { label: 'Key Sent', color: 'info', icon: <Email />, activeStep: 3 },
  pending: { label: 'Pending', color: 'default', icon: <Inventory2 />, activeStep: 1 },
  cancelled: { label: 'Cancelled', color: 'error', icon: <Inventory2 />, activeStep: 0 },
};

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleTrack = async () => {
    if (!orderId.trim()) { setError('Please enter your Order ID.'); return; }
    setError('');
    setLoading(true);
    setOrderData(null);

    const cleanId = orderId.trim();
    const cleanIdLower = cleanId.toLowerCase();
    let matched = null;

    // 1. Search in ar_dashboard_orders
    try {
      const dashOrders = JSON.parse(localStorage.getItem('ar_dashboard_orders') || '[]');
      if (Array.isArray(dashOrders)) {
        matched = dashOrders.find(o =>
          String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanIdLower
        );
      }
    } catch (e) {}

    // 2. Search in all ar_customer_orders_* keys
    if (!matched) {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('ar_customer_orders_')) {
            const data = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(data)) {
              const found = data.find(o =>
                String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanIdLower
              );
              if (found) { matched = found; break; }
            }
          }
        }
      } catch (e) {}
    }

    // 3. Try dedicated backend /trackOrder API directly
    if (!matched) {
      try {
        const resTrack = await invokeApi(apiList.trackOrder || '/trackOrder', { order_id: cleanId, orderId: cleanId });
        if (resTrack?.data?.responseCode === "200" && resTrack?.data?.order) {
          const ord = resTrack.data.order;
          const status = (ord.order_status || 'processing').toLowerCase();
          const isDelivered = ['delivered', 'completed', 'paid'].includes(status);
          const rawItems = ord.items || [];
          const itemsList = Array.isArray(rawItems) && rawItems.length > 0
            ? rawItems.map(i => `${i.product_name || 'Software License'} × ${i.quantity || 1}`)
            : ['Genuine Software License Key × 1'];

          setOrderData({
            id: ord.order_number || ord.id,
            date: ord.created_at ? new Date(ord.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent',
            items: itemsList,
            status: isDelivered ? 'delivered' : (status.includes('cancel') ? 'cancelled' : 'processing'),
            estimatedDelivery: isDelivered ? 'Delivered via Email' : 'Within 5–15 minutes',
            customerName: ord.customer_name || 'Customer',
            email: ord.customer_email || '',
            totalAmount: ord.total_amount ? `₹${parseFloat(ord.total_amount).toLocaleString('en-IN')}` : '',
            timeline: resTrack.data.steps?.map(s => ({
              label: s.status,
              description: s.description,
              date: s.timestamp ? new Date(s.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '',
              done: !!s.done,
            })) || []
          });
          setLoading(false);
          return;
        }
      } catch (e) {}
    }

    // 4. Try backend API — getOrders or getAllProductOrder
    if (!matched) {
      try {
        const res = await invokeGetApi(apiList.getOrders || '/getOrders');
        if (res?.data?.orders && Array.isArray(res.data.orders)) {
          matched = res.data.orders.find(o =>
            String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanIdLower
          );
        }
      } catch (e) {}
    }

    if (!matched) {
      try {
        const res2 = await invokeApi(apiList.getAllProductOrder || '/getAllProductOrder', { search: cleanId });
        if (res2?.data?.orders && Array.isArray(res2.data.orders)) {
          matched = res2.data.orders.find(o =>
            String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanIdLower
          );
        }
      } catch (e) {}
    }

    // 5. Try getAllOrderTrack (tracking-specific API)
    if (!matched) {
      try {
        const res3 = await invokeGetApi(apiList.getAllOrderTrack || '/getAllOrderTrack');
        const tracks = res3?.data?.tracks || [];
        const trackMatch = tracks.find(t =>
          String(t.order_number || '').toLowerCase() === cleanIdLower
        );
        if (trackMatch) {
          // Build order data from tracking entry
          const trackStatus = (trackMatch.current_status || 'Processing').toLowerCase();
          const isDelivered = trackStatus.includes('deliver') || trackStatus.includes('complete');
          setOrderData({
            id: trackMatch.order_number,
            date: trackMatch.steps?.[0]?.timestamp ? new Date(trackMatch.steps[0].timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent',
            items: ['Software Genuine License Key × 1'],
            status: isDelivered ? 'delivered' : trackStatus.includes('cancel') ? 'cancelled' : 'processing',
            estimatedDelivery: isDelivered ? 'Already Delivered' : 'Within 5–10 minutes',
            timeline: trackMatch.steps?.map(s => ({
              label: s.status,
              description: `${s.description || ''} ${s.location ? '— ' + s.location : ''}`.trim(),
              date: s.timestamp ? new Date(s.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '',
              done: true,
            })) || []
          });
          setLoading(false);
          return;
        }
      } catch (e) {}
    }

    if (matched) {
      const orderDate = matched.date || (matched.created_at
        ? new Date(matched.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'Recent');
      const orderStatus = (matched.order_status || matched.status || 'Processing').toLowerCase();
      const isDelivered = orderStatus.includes('deliver') || orderStatus.includes('complete');
      const isCancelled = orderStatus.includes('cancel');

      const rawItems = matched.items || matched.order_items || [];
      const itemsList = Array.isArray(rawItems) && rawItems.length > 0
        ? rawItems.map(i => `${i.product_name || i.description || i.name || 'Software License'} × ${i.quantity || 1}`)
        : ['Software Genuine License Key × 1'];

      const totalVal = parseFloat(matched.total_amount || matched.totalAmount || matched.total || 0);

      setOrderData({
        id: matched.order_number || matched.invoice_number || matched.id || cleanId.toUpperCase(),
        date: orderDate,
        items: itemsList,
        total: totalVal ? `₹${totalVal.toLocaleString('en-IN')}` : 'Paid',
        customerName: matched.customer_name || matched.customerName || 'Customer',
        email: matched.customer_email || matched.customerEmail || matched.email || '',
        status: isCancelled ? 'cancelled' : isDelivered ? 'delivered' : 'processing',
        estimatedDelivery: isDelivered ? 'Already Delivered' : isCancelled ? 'Order Cancelled' : 'Within 5–10 minutes',
        timeline: [
          {
            label: 'Order Placed',
            description: 'Your order was received and confirmed.',
            date: orderDate,
            done: true
          },
          {
            label: 'Payment Verified',
            description: `Payment of ${totalVal ? `₹${totalVal.toLocaleString('en-IN')}` : 'Prepaid'} successfully verified.`,
            date: orderDate,
            done: true
          },
          {
            label: 'License Key Generated',
            description: isDelivered
              ? 'Your genuine license key has been prepared.'
              : isCancelled
                ? 'Order was cancelled before key generation.'
                : 'Your license key is being prepared.',
            date: isDelivered ? orderDate : isCancelled ? 'Cancelled' : 'In progress...',
            done: isDelivered
          },
          {
            label: 'Email Dispatched',
            description: isDelivered
              ? `License key and activation guide sent to ${matched.customer_email || matched.email || 'your email'}.`
              : isCancelled
                ? 'Delivery cancelled.'
                : 'License key will be sent to your inbox.',
            date: isDelivered ? orderDate : isCancelled ? 'Cancelled' : 'Pending',
            done: isDelivered
          },
        ]
      });
    } else {
      setError('No order found with this Order ID. Please double-check and try again.');
    }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(orderData?.id || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentStatus = orderData ? (statusConfig[orderData.status] || statusConfig.pending) : null;

  return (
    <Box sx={{ py: 8, minHeight: '75vh', bgcolor: '#F8FAFC' }}>
      <SEO
        title="Track Your Order — AR Infotech"
        description="Track your software license order from AR Infotech. Enter your order ID to get real-time updates."
      />
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Typography color="text.primary">Track Order</Typography>
        </Breadcrumbs>

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <LocalShipping sx={{ fontSize: 56, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>Track Your Order</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              Enter your Order ID to get the latest status on your software license delivery.
            </Typography>
          </motion.div>
        </Box>

        <Paper
          elevation={0}
          sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid #E2E8F0', mb: 4 }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                label="Order ID *"
                placeholder="e.g. ORD-123456"
                value={orderId}
                onChange={e => setOrderId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTrack()}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleTrack}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                  '&:hover': { background: 'linear-gradient(135deg, #0d47a1, #0958b8)' }
                }}
              >
                {loading ? 'Tracking...' : 'Track'}
              </Button>
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>{error}</Alert>}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Your Order ID can be found in the confirmation email sent to your inbox.
          </Typography>
        </Paper>

        <AnimatePresence>
          {orderData && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  border: '1.5px solid',
                  borderColor: currentStatus.color === 'success' ? '#A7F3D0' : currentStatus.color === 'warning' ? '#FDE68A' : currentStatus.color === 'error' ? '#FECACA' : '#BFDBFE',
                  bgcolor: currentStatus.color === 'success' ? '#F0FDF4' : currentStatus.color === 'warning' ? '#FFFBEB' : currentStatus.color === 'error' ? '#FEF2F2' : '#EFF6FF',
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{orderData.id}</Typography>
                      <Box onClick={handleCopy} sx={{ cursor: 'pointer', color: 'text.secondary', display: 'flex' }} title="Copy order ID">
                        <ContentCopy sx={{ fontSize: 18 }} />
                      </Box>
                      {copied && <Typography variant="caption" color="success.main">Copied!</Typography>}
                    </Box>
                    <Typography color="text.secondary" variant="body2">Placed on {orderData.date}</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>Items: {orderData.items.join(', ')}</Typography>
                    {orderData.total && (
                      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>Total: <strong>{orderData.total}</strong></Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: { sm: 'right' } }}>
                    <Chip icon={currentStatus.icon} label={currentStatus.label} color={currentStatus.color} sx={{ fontWeight: 700, mb: 1, px: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Estimated: <strong>{orderData.estimatedDelivery}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #E2E8F0', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Order Timeline</Typography>
                <Stepper activeStep={currentStatus.activeStep} orientation="vertical">
                  {orderData.timeline.map((step) => (
                    <Step key={step.label} completed={step.done}>
                      <StepLabel StepIconProps={{ sx: { color: step.done ? 'success.main !important' : undefined, '&.Mui-active': { color: 'primary.main' } } }}>
                        <Typography sx={{ fontWeight: step.done ? 700 : 500 }}>{step.label}</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>{step.date}</Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #E2E8F0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <SupportAgent sx={{ color: 'primary.main', fontSize: 36 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Need Help?</Typography>
                    <Typography variant="body2" color="text.secondary">Our team is available 24/7 for activation assistance.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button variant="contained" component="a" href="https://wa.me/919113299131" target="_blank" size="small" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                    WhatsApp Support
                  </Button>
                  <Button variant="outlined" component="a" href="/contact" size="small" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                    Contact Us
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {!orderData && !loading && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.disabled">
              Enter your Order ID from your confirmation email or order history to track your order status in real time.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TrackOrderPage;
